import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Loader2, Plus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * AddToCollectionButton — drop-in button that lets paid members add a target
 * (encounter / species / media) to one of their collections via a small popover.
 */
export function AddToCollectionButton({ targetType, targetId, className = "" }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [collections, setCollections] = useState(null); // null = not loaded
    const [busy, setBusy] = useState(false);
    const popRef = useRef(null);

    const eligible = user && (
        user.role === "admin"
        || (["verified_witness", "research_council"].includes(user.subscription_tier))
    );

    useEffect(() => {
        if (!open) return;
        if (collections !== null) return;
        api.get("/collections/mine")
            .then((r) => setCollections(r.data?.items || []))
            .catch(() => setCollections([]));
    }, [open, collections]);

    useEffect(() => {
        function onDoc(e) { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); }
        if (open) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    async function addTo(cid) {
        setBusy(true);
        try {
            await api.post(`/collections/${cid}/items`, { target_type: targetType, target_id: targetId });
            toast.success("Added to collection");
            setOpen(false);
        } catch (e) {
            toast.error("Could not add");
        } finally {
            setBusy(false);
        }
    }

    async function createAndAdd() {
        const name = window.prompt("New collection name:");
        if (!name || !name.trim()) return;
        setBusy(true);
        try {
            const { data: c } = await api.post("/collections", { name: name.trim(), is_public: true });
            await api.post(`/collections/${c.id}/items`, { target_type: targetType, target_id: targetId });
            setCollections((cur) => cur ? [c, ...cur] : [c]);
            toast.success("Collection created · item added");
            setOpen(false);
        } catch (e) { toast.error("Could not create"); }
        finally { setBusy(false); }
    }

    if (!eligible) {
        return (
            <Link to="/pricing" className={`ok-btn-outline inline-flex items-center gap-1 ${className}`} data-testid="add-to-collection-locked">
                <Bookmark className="w-4 h-4" /> Save · Members
            </Link>
        );
    }

    return (
        <div className="relative inline-block" ref={popRef}>
            <button
                onClick={() => setOpen((v) => !v)}
                className={`ok-btn-outline inline-flex items-center gap-1 ${className}`}
                data-testid="add-to-collection-btn"
            >
                <Bookmark className="w-4 h-4" /> Save
            </button>
            {open && (
                <div className="absolute z-50 right-0 mt-2 w-72 ok-card p-3 shadow-xl" data-testid="add-to-collection-pop">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-2">Add to collection</div>
                    {collections === null ? (
                        <div className="text-xs opacity-60 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
                    ) : collections.length === 0 ? (
                        <div className="text-xs opacity-70 mb-2">No collections yet. Create your first one:</div>
                    ) : (
                        <ul className="space-y-1 max-h-56 overflow-y-auto">
                            {collections.map((c) => {
                                const already = (c.items || []).some((i) => i.target_type === targetType && i.target_id === targetId);
                                return (
                                    <li key={c.id}>
                                        <button
                                            disabled={busy || already}
                                            onClick={() => addTo(c.id)}
                                            className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-[var(--ok-surface-2)] flex items-center justify-between gap-2 ${already ? "opacity-60" : ""}`}
                                            data-testid={`add-to-collection-option-${c.id}`}
                                        >
                                            <span className="truncate">{c.name}</span>
                                            {already ? <Check className="w-3 h-3 text-[var(--ok-accent)] flex-shrink-0" /> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <button
                        onClick={createAndAdd}
                        disabled={busy}
                        className="ok-btn-primary text-xs w-full mt-2 inline-flex items-center justify-center gap-1"
                        data-testid="add-to-collection-new"
                    >
                        <Plus className="w-3 h-3" /> New collection
                    </button>
                </div>
            )}
        </div>
    );
}
